/**
 * CR-7105: Shared read-only view of a delivery appeal's content — the
 * claimed-vs-current table, the appellant's note, and the appeal's existing
 * attachments. Extracted from AppealResponseModal so the raiser's View
 * Appeal modal and the responder's Respond modal render the identical
 * record; before this the raiser had no view at all and the responder was
 * never shown the attached evidence (upload-only).
 */
import { useEffect, useState } from 'react';
import { Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { purchasingAPI, DeliveryAppeal } from '../../../lib/api';
import type { DeliveryAppealAttachment } from '../../../lib/api/types/grn-types';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface AppealDetailsContentProps {
  appeal: DeliveryAppeal;
}

export default function AppealDetailsContent({ appeal }: AppealDetailsContentProps) {
  const [attachments, setAttachments] = useState<DeliveryAppealAttachment[]>([]);
  const { formatDate } = useFormatters();

  useEffect(() => {
    let cancelled = false;
    purchasingAPI
      .getAppealAttachments(appeal.id)
      .then((res) => {
        if (!cancelled) setAttachments(res.data);
      })
      .catch(() => {
        // Attachment list is supplementary — the appeal content still renders.
      });
    return () => {
      cancelled = true;
    };
  }, [appeal.id]);

  const handleDownload = async (att: DeliveryAppealAttachment) => {
    try {
      const res = await purchasingAPI.downloadAppealAttachment(appeal.id, att.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = att.file_name;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download attachment');
    }
  };

  return (
    <>
      <div className="bg-gray-50 rounded-lg p-3 text-sm grid grid-cols-2 gap-2">
        <div><span className="text-gray-500">GRN:</span> <span className="font-medium">{appeal.grn_number}</span></div>
        <div><span className="text-gray-500">PO:</span> <span className="font-medium">{appeal.po_number}</span></div>
        <div><span className="text-gray-500">Part:</span> <span className="font-medium">{appeal.part_number}</span></div>
        <div><span className="text-gray-500">Appeal #:</span> <span className="font-medium">{appeal.appeal_sequence}</span></div>
      </div>

      {/* Claimed vs Current Values */}
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Field</th>
              <th className="px-3 py-2 text-left font-medium">Current</th>
              <th className="px-3 py-2 text-left font-medium text-orange-600">Claimed</th>
            </tr>
          </thead>
          <tbody>
            {appeal.qty_claimed && (
              <tr className="border-t">
                <td className="px-3 py-2">Quantity</td>
                <td className="px-3 py-2">{appeal.grn_qty_received}</td>
                <td className="px-3 py-2 font-medium text-orange-600">{appeal.qty_claimed}</td>
              </tr>
            )}
            {appeal.date_claimed && (
              <tr className="border-t">
                <td className="px-3 py-2">Delivery Date</td>
                <td className="px-3 py-2">{appeal.original_receipt_date ? formatDate(appeal.original_receipt_date) : appeal.grn_receipt_date ? formatDate(appeal.grn_receipt_date) : '-'}</td>
                <td className="px-3 py-2 font-medium text-orange-600">{formatDate(appeal.date_claimed)}</td>
              </tr>
            )}
            {appeal.odd_claimed && (
              <tr className="border-t">
                <td className="px-3 py-2">ODD</td>
                {/* Current ODD = the date the delivery was judged against
                    (grn_original_delivery_date is the verdict anchor), not
                    the line's re-planned delivery date (grn_delivery_date). */}
                <td className="px-3 py-2">{(appeal.grn_original_delivery_date ?? appeal.grn_delivery_date) ? formatDate((appeal.grn_original_delivery_date ?? appeal.grn_delivery_date) as string) : '-'}</td>
                <td className="px-3 py-2 font-medium text-orange-600">{formatDate(appeal.odd_claimed)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {appeal.supplier_note && (
        <div>
          <label className="text-sm font-medium text-gray-500">
            {appeal.is_customer_appeal ? 'Customer Note' : 'Supplier Note'}
          </label>
          <p className="mt-1 text-sm bg-orange-50 p-3 rounded-lg">{appeal.supplier_note}</p>
        </div>
      )}

      {appeal.customer_note && (
        <div>
          <label className="text-sm font-medium text-gray-500">
            {appeal.is_customer_appeal ? 'Supplier Response' : 'Customer Response'}
          </label>
          <p className="mt-1 text-sm bg-blue-50 p-3 rounded-lg">{appeal.customer_note}</p>
        </div>
      )}

      {attachments.length > 0 && (
        <div>
          <label className="text-sm font-medium text-gray-500 flex items-center gap-1">
            <Paperclip className="h-3.5 w-3.5" />
            Attachments
          </label>
          <div className="mt-1 space-y-1">
            {attachments.map((att) => (
              <button
                key={att.id}
                onClick={() => handleDownload(att)}
                className="block text-sm text-blue-600 hover:underline text-left"
                title={att.file_name}
              >
                {att.file_name}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
